import React, { useRef, useState } from 'react';
import { Button } from 'antd';
import { ReactComponent as ButtonUploadIcon } from 'assets/icons/ButtonUpload.svg';
import { ReactComponent as DeleteOutlinedIcon } from 'assets/icons/DeleteOutlined.svg';

import {
  CRMDealCategory,
  DealDocument,
  DocumentsTransactionResponse,
} from '_business/types/TransactionsDocuments';
import FileLine from './FileLine';
import humanFileSize from '_technical/utils/fileSize';
import { formatDate } from '_technical/utils/date';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from 'views/projects/products_hooks';
import { getCategoryToUpload } from '_business/const/DocumentsCategories';
import { Deal } from '_business/types/TransactionDetails';
import { DOCUMENT_TYPES } from '_business/const/Files';

interface ButtonUploadProps {
  label: string;
  badgeText?: string;
  onClick?: () => void;
  className?: string;
  filesKeys: (keyof CRMDealCategory)[];
  documentsData: DocumentsTransactionResponse;
  deal: Deal;
}
type FileToUpload = {
  file: File;
  id: string;
};
const ButtonUpload: React.FC<ButtonUploadProps> = ({
  label,
  badgeText,
  className = '',
  filesKeys,
  documentsData,
  deal,
}) => {
  const { t } = useTranslation();
  const { mutate, isPending: isLoading } = useUploadFile();

  const dealFiles = documentsData.deal.CRM.deal;
  const files = documentsData.files;
  const filesData = filesKeys
    .map((key, index) => dealFiles[key]?.split(';') || [])
    .flat();

  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputKey, setInputKey] = useState(0);

  const handleClick = () => {
    inputRef.current?.click();
  };
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    const files = event.target.files;
    if (!files?.length) {
      return setFilesToUpload([]);
    }
    const _filesToUpload = Array.from({ length: files.length })
      .map((_, i) => {
        const file = files.item(i);
        if (!file) {
          return undefined;
        }
        return {
          file,
          id: i
            .toString()
            .concat(file.name)
            .concat(file.lastModified.toString()),
        };
      })
      .filter((item): item is FileToUpload => !!item);
    setFilesToUpload(_filesToUpload);
  };
  const handleDelete = (id: string) => {
    setFilesToUpload(filesToUpload.filter((i) => i.id !== id));
  };

  const handleCancel = () => {
    setInputKey(inputKey + 1);
    setFilesToUpload([]);
  };

  const handleUpload = () => {
    mutate(
      {
        category: getCategoryToUpload(filesKeys, deal.dealstage.label),
        files: filesToUpload.map(({ file }) => file),
        transactionId: dealFiles.hs_object_id,
      },
      {
        onSuccess: handleCancel,
      },
    );
  };

  return (
    <span>
      <input
        key={inputKey}
        onChange={handleChange}
        ref={inputRef}
        type="file"
        className={'hidden'}
        multiple
        accept={DOCUMENT_TYPES}
      />
      <span
        className={`px-3 pt-0.5 h-auto flex flex-col gap-2 rounded-lg border border-BeigeHellio-6 bg-white w-full ${className}`}
      >
        <div className={`flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 translate-y-0.5">
            <span className="text-GrisHellio-6 text-sm font-semibold leading-none">
              {label}
            </span>
            {badgeText && (
              <span className="p-1 bg-JauneHellio-1 text-xs font-normal leading-none rounded mt-1">
                {badgeText}
              </span>
            )}
          </div>
          <span>
            <Button
              onClick={handleClick}
              className={'border-none translate-y-[3px]'}
              icon={<ButtonUploadIcon />}
            />
          </span>
        </div>
        {filesToUpload && filesToUpload.length > 0 && (
          <span>
            {filesToUpload.map(({ file, id }, i) => {
              if (!file) return null;
              const description = `${file.name.split('.').pop()?.toUpperCase()} - ${humanFileSize(file.size)}, ${formatDate(new Date(), "dd MMM yyyy hh'h'mm")}`;
              return (
                <div
                  key={id}
                  className={`flex justify-between items-center pr-2`}
                >
                  <span className={'flex flex-col items-center'}>
                    <span className={'flex items-center'}>
                      <Button
                        disabled={isLoading}
                        onClick={() => handleDelete(id)}
                        className={'border-none'}
                        icon={<DeleteOutlinedIcon className="h-4" />}
                      />
                      <span
                        className={
                          'text-GrisHellio-6 text-xs line-clamp-1 text-ellipsis'
                        }
                      >
                        {file.name}
                      </span>
                    </span>
                    <span
                      className={'text-xs text-GrisHellio-5 block lg:hidden'}
                    >
                      {description}
                    </span>
                  </span>
                  <span className={'text-xs text-GrisHellio-5 lg:block hidden'}>
                    {description}
                  </span>
                </div>
              );
            })}
            <span className={'flex justify-center gap-2 py-3'}>
              <Button
                disabled={isLoading}
                variant={'outlined'}
                color={'primary'}
                onClick={handleCancel}
              >
                {t('global:cancel')}
              </Button>
              <Button
                onClick={handleUpload}
                disabled={isLoading}
                loading={isLoading}
                type={'primary'}
              >
                {t('global:validate')}
              </Button>
            </span>
          </span>
        )}
        {filesData && (
          <table className={'w-full'}>
            <tbody>
              {filesData
                // map to files and sort by lastModified
                .map((key) => {
                  const file = files.find((file) => file.id === key);
                  if (!file) return null;
                  return file;
                })
                .filter((file): file is DealDocument => !!file)
                .sort(
                  (a, b) =>
                    new Date(b.updatedAt).getTime() -
                    new Date(a.updatedAt).getTime(),
                )
                .map((file) => (
                  <FileLine key={file.id} file={file} />
                ))}
            </tbody>
          </table>
        )}
      </span>
    </span>
  );
};

export default ButtonUpload;
